import { GfCompositeError } from '../errors';
import { isDef, isFunc } from '../utils/common';
import { CPRSignal } from '../utils/CPRController';
import { Deferred } from './Deferred';

export interface ParallelOptions {
	// Defaults to 1
	concurrency?: number;

	// By default, the parallel functions will wait for any ongoing operations before rejecting
	returnImmediatelyOnErrorOrCancel?: boolean;

	cprSignal?: CPRSignal;
}

/**
 * Allows async processing of an iterable; returns results to a second callback.
 * Result order is not guaranteed to match input order
 *
 * Marked as async, even though it doesn't need to be, so that the initial errors are not sync
 * https://stackoverflow.com/questions/21887856/should-an-async-api-ever-throw-synchronously
 *
 */
export async function piterate<T, U>(
	iterable: Iterable<T>,
	fnMapper: (item: T, index: number) => Promise<U> | U,
	options?: ParallelOptions
): Promise<void>;
export async function piterate<T, U>(
	iterable: Iterable<T>,
	fnMapper: (item: T, index: number) => Promise<U> | U,
	fnResult?: (result: U, index: number) => void,
	options?: ParallelOptions
): Promise<void>;
export async function piterate<T, U>(
	iterable: Iterable<T>,
	fnMapper: (item: T, index: number) => Promise<U> | U,
	fnResultOrOptions?: ((result: U, index: number) => void) | ParallelOptions,
	options?: ParallelOptions
): Promise<void> {
	let fnResult: ((result: U, index: number) => void) | undefined;

	if (fnResultOrOptions) {
		if (isFunc(fnResultOrOptions)) {
			fnResult = fnResultOrOptions;
		} else {
			options = fnResultOrOptions;
		}
	}

	const { concurrency = 1, returnImmediatelyOnErrorOrCancel = false, cprSignal } = options ?? {};

	if (!((Number.isSafeInteger(concurrency) || concurrency === Infinity) && concurrency >= 1)) {
		throw new TypeError(
			`Expected 'concurrency' to be an integer from 1 and up or \`Infinity\`, got \`${concurrency}\` (${typeof concurrency})`
		);
	}

	if (cprSignal?.canceled) {
		if (isDef(cprSignal.reason)) {
			throw new GfCompositeError('cprSignal already canceled', [cprSignal.reason]);
		} else {
			// Just return immediately
			return Promise.resolve();
		}
	}

	let nextIndex = 0;
	let numWaiting = 0;
	let doneIteratingInput = false;
	let canceled: boolean;
	let errored: boolean;
	const errors: any[] = [];

	const iterator = iterable[Symbol.iterator]();

	const deferred = new Deferred<void>();

	const resolve = () => {
		cprSignal?.off('cancel', onCancel);
		cprSignal?.off('resume', onCancel);
		deferred.resolve();
	};

	const reject = (err: any) => {
		cprSignal?.off('cancel', onCancel);
		cprSignal?.off('resume', onCancel);
		deferred.reject(err);
	};

	const onCancel = () => {
		if (!canceled) {
			canceled = true;
			doneIteratingInput = true;

			if (returnImmediatelyOnErrorOrCancel) {
				if (isDef(cprSignal?.reason)) {
					reject(new GfCompositeError('cprSignal canceled', [cprSignal?.reason]));
				} else {
					// Soft cancel
					resolve();
				}
			} else {
				// We still want to wait for pending stuff...
				if (isDef(cprSignal?.reason)) {
					errors.push(cprSignal?.reason);
					rejectWithStoredErrorsIfNothingPending();
				} else if (numWaiting === 0 && doneIteratingInput) {
					// No operations are running, and no more are on the stack. Done!
					resolve();
				}
			}
		}
	};

	const onResume = () => {
		maybeRejectOrRunMore();
	};

	const rejectWithStoredErrorsIfNothingPending = () => {
		if (numWaiting === 0) {
			// We had pending things, but all are done so we can reject
			if (errors.length === 1) {
				reject(errors[0]);
			} else {
				reject(
					new GfCompositeError('piterate(): received multiple errors in parallel', errors)
				);
			}
		}
	};

	const onError = (err: any) => {
		errored = true;

		if (returnImmediatelyOnErrorOrCancel) {
			reject(err);
		} else {
			errors.push(err);
			rejectWithStoredErrorsIfNothingPending();
		}
	};

	const maybeRejectOrRunMore = () => {
		if (errors.length) {
			rejectWithStoredErrorsIfNothingPending();
		} else {
			maybeRunMore();
		}
	};

	const maybeRunMore = (): void => {
		while (
			!errored &&
			!canceled &&
			!doneIteratingInput &&
			numWaiting < concurrency &&
			!cprSignal?.paused
		) {
			const nextItem = iterator.next();

			if (nextItem.done) {
				doneIteratingInput = true;
			} else {
				// Do something with the input
				++numWaiting;

				// Fire off but don't wait
				const _index = nextIndex++;

				let valueOrPromise;

				try {
					valueOrPromise = fnMapper(nextItem.value, _index);
				} catch (syncError) {
					--numWaiting;
					onError(syncError);
					return;
				}

				Promise.resolve(valueOrPromise).then(
					result => {
						if (!errored && !canceled) {
							fnResult?.(result, _index);
						}

						--numWaiting;

						maybeRejectOrRunMore();
					},
					err => {
						--numWaiting;
						onError(err);
					}
				);
			}
		}

		if (numWaiting === 0 && doneIteratingInput) {
			// No operations are running, and no more to read, so either there were
			// no inputs, or they were sync & no errors...
			resolve();
		}
	};

	cprSignal?.on('cancel', onCancel);
	cprSignal?.on('resume', onResume);
	maybeRunMore();

	return deferred.promise;
}
